import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@repo/ui/src/components/ui/card';
import { Button } from '@repo/ui/src/components/ui/button';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Checkbox } from '@repo/ui/src/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@repo/ui/src/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@repo/ui/src/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { MapPin, Plus, TriangleAlert } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';
import type { BranchDto } from '@repo/shared-types';
import {
  useBranches,
  useCreateBranch,
  useRenameBranch,
  useArchiveBranch,
  useUnarchiveBranch,
  useAssignUnassigned,
} from '../../../hooks/useBranches';
import { useSubscription } from '../../../hooks/useSubscription';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import { visibleBranches } from './branchesTabHelpers';

export function BranchesTab() {
  const { t } = useTranslation('settings');
  const [showArchived, setShowArchived] = useState(false);
  // Always fetch the full (active + archived) list — the toggle's own
  // visibility keys off `allBranches`, not off the filtered view, so an org
  // with zero active branches never loses its way back to the archived ones
  // (THI-137 review). `visibleBranches` does the filtering for display.
  const { data, isLoading } = useBranches({ includeArchived: true });
  const allBranches = data?.branches ?? [];
  const branches = visibleBranches(allBranches, showArchived);
  const { data: subscription, isPending: subscriptionPending } = useSubscription();
  // Downgrading never deletes a branch — it only blocks GROWING the live
  // count: creating one, and unarchiving one (BranchRow, THI-172). The list
  // below stays fully manageable (rename/archive) on any plan.
  const canAdd = !!subscription?.plan.allowsBranches;
  // `undefined` is "not loaded yet", NOT "not allowed": while the subscription
  // query is in flight `canAdd` is false, and a Center admin saw the "nâng gói"
  // card flash before it vanished (THI-137 final review F7). The Add button may
  // stay hidden until we know — a control that appears late is normal — but an
  // upsell that appears and retracts reads as a bug and, worse, as a claim the
  // customer is on the wrong plan.
  const showUpsell = !canAdd && !subscriptionPending;
  const openUpgradeDialog = useUpgradeDialogStore((s) => s.open);
  const [addOpen, setAddOpen] = useState(false);
  const unassignedSlotCount = data?.unassignedSlotCount ?? 0;
  // The FIRST branch is the adopt moment; the second and later are routine.
  // `allBranches` includes archived ones on purpose — an org that made a branch,
  // archived it and starts again is not making its first.
  const isFirstBranch = allBranches.length === 0;
  const [bulkOpen, setBulkOpen] = useState(false);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">{t('branches.title')}</CardTitle>
          <CardDescription className="mt-1">{t('branches.description')}</CardDescription>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {allBranches.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-[44px] px-3 text-xs font-medium"
              onClick={() => setShowArchived((v) => !v)}
            >
              {t(showArchived ? 'branches.hideArchived' : 'branches.showArchived')}
            </Button>
          )}
          {canAdd && (
            <Button size="sm" className="h-[44px] text-xs font-medium" onClick={() => setAddOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.8} />
              {t('branches.add')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Only while N > 0 AND there is an active branch to file them under —
            an org with zero active branches (never adopted, or every branch
            archived) cannot be "filtered by branch" yet, so the warning
            wouldn't describe anything real and "Gán hàng loạt" would open an
            empty Select (final review F1). Ochre because these classes are
            silently invisible under every branch filter — the one thing a
            lens cannot show you (INVARIANTS §6). */}
        {unassignedSlotCount > 0 && allBranches.some((b) => b.archivedAt === null) && (
          <div className="mb-4 flex flex-wrap items-center gap-[9.6px] rounded-xl border border-attention-border bg-attention-tint p-[12.8px]">
            <TriangleAlert className="size-4 shrink-0 text-attention-foreground" strokeWidth={1.9} />
            <p className="min-w-[180px] flex-1 text-[13px] leading-normal text-attention-foreground">
              {t('branches.unassignedWarning', { count: unassignedSlotCount })}
            </p>
            <Button
              className="h-[44px] shrink-0 bg-attention px-[12.8px] text-[12.5px] font-semibold text-attention-foreground hover:bg-attention/90"
              onClick={() => setBulkOpen(true)}
            >
              {t('branches.bulkAssign')}
            </Button>
          </div>
        )}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 bg-surface-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : branches.length > 0 ? (
          // THI-172: dormant WITH branches shows this list, not the empty
          // state below — a non-Center org's chi nhánh are kept, not hidden.
          // The empty state is reserved for when there genuinely are none.
          <div className="space-y-3">
            {branches.map((b) => (
              <BranchRow key={b.id} branch={b} canAdd={canAdd} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-14 h-14 bg-surface-100 flex items-center justify-center mx-auto mb-3">
              <MapPin className="w-6 h-6 text-surface-400" strokeWidth={1.8} />
            </div>
            <p className="text-sm font-medium text-surface-700 mb-1">{t('branches.empty')}</p>
            <p className="text-xs text-surface-400 max-w-xs mx-auto">{t('branches.emptyDescription')}</p>
          </div>
        )}

        {/* Dormant, not a fence: the list above stays regardless of plan.
            Only creating a NEW branch and unarchiving one are gated — see
            canAdd/showUpsell above and BranchRow's Khôi phục button below; the
            API 403s both identically (`requireBranchesFeature`). The compare
            dialog highlights the multi-branch row via the FEATURE_TO_ROW entry
            landing in Task 6. */}
        {showUpsell && (
          <div className="mt-4 rounded-lg border border-surface-200 p-4">
            <p className="text-sm font-medium text-surface-900">{t('branches.upsellTitle')}</p>
            <p className="mt-1 text-xs text-surface-500">{t('branches.upsellBody')}</p>
            <Button variant="outline" className="mt-3 h-[44px]" onClick={() => openUpgradeDialog('branches')}>
              {t('branches.upsellCta')}
            </Button>
          </div>
        )}
      </CardContent>

      <AddBranchDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        isFirstBranch={isFirstBranch}
        unassignedSlotCount={unassignedSlotCount}
      />
      {/* A plain sibling Dialog, never nested inside another dialog's content
          (INVARIANTS §6). */}
      <BulkAssignDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        branches={allBranches.filter((b) => b.archivedAt === null)}
        count={unassignedSlotCount}
      />
    </Card>
  );
}

function BranchRow({ branch, canAdd }: { branch: BranchDto; canAdd: boolean }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const [renameOpen, setRenameOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const archiveMutation = useArchiveBranch();
  const unarchiveMutation = useUnarchiveBranch();
  const isArchived = !!branch.archivedAt;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-surface-200">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-surface-900 truncate">{branch.name}</p>
          {isArchived && (
            <Badge variant="outline" className="text-surface-500 border-surface-200 bg-surface-50">
              {t('branches.archivedBadge')}
            </Badge>
          )}
        </div>
        <p className="text-xs text-surface-400">{t('branches.slotCount', { count: branch.slotCount })}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-[44px] text-xs font-medium"
          onClick={() => setRenameOpen(true)}
        >
          {t('branches.rename')}
        </Button>
        {isArchived ? (
          // THI-172: Khôi phục GROWS the live branch count exactly like Thêm
          // chi nhánh does, so it is gated identically — disabled (not
          // hidden) so a dormant org still sees the row exists, with the
          // upsell card below explaining why it is off.
          <Button
            variant="outline"
            size="sm"
            className="h-[44px] text-xs font-medium"
            onClick={() => unarchiveMutation.mutate(branch.id)}
            disabled={unarchiveMutation.isPending || !canAdd}
          >
            {t('branches.unarchive')}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-[44px] text-xs font-medium text-surface-500 hover:text-red-600"
            onClick={() => setArchiveOpen(true)}
          >
            {t('branches.archive')}
          </Button>
        )}
      </div>

      <RenameBranchDialog branch={branch} open={renameOpen} onOpenChange={setRenameOpen} />

      {/* Sibling of the row, not nested inside RenameBranchDialog's
          DialogContent (INVARIANTS §6) — an AlertDialog opened from inside
          another dialog's content must stay a JSX descendant of it, and the
          simplest way to satisfy that here is to never nest them at all. */}
      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('branches.archiveTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('branches.archiveBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveMutation.mutate(branch.id)}
              disabled={archiveMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('branches.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddBranchDialog({
  open, onOpenChange, isFirstBranch, unassignedSlotCount,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  isFirstBranch: boolean;
  unassignedSlotCount: number;
}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const { mutate: create, isPending } = useCreateBranch();
  const [name, setName] = useState('');
  // Default ON. The design is not the checkbox — it is the sentence under it:
  // `assignAllHelp` says what happens AND that it is reversible, and the ochre
  // `assignAllOffWarning` appears ONLY when the teacher unchecks, never
  // unprompted. Without the second sentence a pre-ticked box reads as a trap;
  // with it, the default reads as the safe answer it is.
  const [assignAll, setAssignAll] = useState(true);
  // Only offer the adopt when there is something to adopt.
  const showAdopt = isFirstBranch && unassignedSlotCount > 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create(
      { name, ...(showAdopt && assignAll ? { assignAllUnassigned: true } : {}) },
      {
        onSuccess: () => {
          onOpenChange(false);
          setName('');
          setAssignAll(true);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t(isFirstBranch ? 'branches.addFirstTitle' : 'branches.addTitle')}</DialogTitle>
          {isFirstBranch && <DialogDescription>{t('branches.addFirstDesc')}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit}>
          <div className="grid gap-2 py-4">
            <Label htmlFor="branch-name">{t('branches.nameLabel')}</Label>
            <Input
              id="branch-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('branches.namePlaceholder')}
              required
              className="h-[44px]"
            />
          </div>

          {showAdopt && (
            <>
              <div
                className={cn(
                  'rounded-xl border p-[12.8px]',
                  assignAll ? 'border-brand-200 bg-brand-500/[0.06]' : 'border-surface-200',
                )}
              >
                <div className="flex items-start gap-[9.6px]">
                  <Checkbox
                    id="assign-all"
                    checked={assignAll}
                    onCheckedChange={(v) => setAssignAll(v === true)}
                    className="mt-[1px] size-[18px] shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <Label
                      htmlFor="assign-all"
                      className={cn(
                        'block text-[13.5px] leading-snug',
                        assignAll ? 'font-semibold text-surface-800' : 'font-medium text-surface-600',
                      )}
                    >
                      {t('branches.assignAllLabel', { count: unassignedSlotCount })}
                    </Label>
                    {assignAll && (
                      <p className="mt-[6.4px] text-[12.5px] leading-relaxed text-surface-600 text-pretty">
                        {t('branches.assignAllHelp')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Only when the teacher unchecks — the consequence, stated plainly.
                  This box is what makes the default feel safe, so it must never
                  appear before they have chosen anything. */}
              {!assignAll && (
                <div className="mt-[9.6px] flex items-start gap-[9.6px] rounded-xl border border-attention-border bg-attention-tint p-[12.8px]">
                  <TriangleAlert className="mt-[1px] size-4 shrink-0 text-attention-foreground" strokeWidth={1.9} />
                  <p className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-attention-foreground text-pretty">
                    {t('branches.assignAllOffWarning', { count: unassignedSlotCount })}
                  </p>
                </div>
              )}
            </>
          )}

          <DialogFooter className="mt-[19.2px]">
            <Button type="button" variant="outline" className="h-[44px]" onClick={() => onOpenChange(false)}>
              {tc('cancel')}
            </Button>
            <Button type="submit" className="h-[44px]" disabled={isPending || !name.trim()}>
              {t(isFirstBranch ? 'branches.create' : 'branches.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkAssignDialog({
  open, onOpenChange, branches, count,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  branches: BranchDto[];
  count: number;
}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const { mutate: assign, isPending } = useAssignUnassigned();
  // Active branches only: assigning classes into an archived cơ sở would make
  // them vanish from the switcher the moment they arrived (the server rejects
  // it too — this is the UI half of the same rule).
  const [target, setTarget] = useState('');

  const submit = () => {
    if (!target) return;
    assign(target, { onSuccess: () => { onOpenChange(false); setTarget(''); } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('branches.bulkAssignTitle')}</DialogTitle>
          <DialogDescription>{t('branches.bulkAssignDesc', { count })}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-4">
          <Label htmlFor="bulk-target">{t('branches.bulkAssignTarget')}</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger id="bulk-target" className="h-[44px]">
              <SelectValue placeholder={t('branches.bulkAssignTarget')} />
            </SelectTrigger>
            <SelectContent>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" className="h-[44px]" onClick={() => onOpenChange(false)}>
            {tc('cancel')}
          </Button>
          <Button type="button" className="h-[44px]" disabled={isPending || !target} onClick={submit}>
            {t('branches.bulkAssign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameBranchDialog({
  branch, open, onOpenChange,
}: {
  branch: BranchDto;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('settings');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('branches.renameTitle')}</DialogTitle>
        </DialogHeader>
        {/* Mounted only while open so the field always initializes fresh from
            the current branch name (no effect-driven resync needed) — same
            pattern as SubjectsPage's EditSubjectDialog. */}
        {open && <RenameBranchForm branch={branch} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

function RenameBranchForm({
  branch, onOpenChange,
}: {
  branch: BranchDto;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const { mutate: rename, isPending } = useRenameBranch(branch.id);
  const [name, setName] = useState(branch.name);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    rename({ name }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <form onSubmit={submit}>
      <div className="grid gap-2 py-4">
        <Label htmlFor="rename-branch-name">{t('branches.nameLabel')}</Label>
        <Input
          id="rename-branch-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="h-[44px]"
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" className="h-[44px]" onClick={() => onOpenChange(false)}>
          {tc('cancel')}
        </Button>
        <Button type="submit" className="h-[44px]" disabled={isPending || !name.trim()}>
          {tc('save')}
        </Button>
      </DialogFooter>
    </form>
  );
}
